import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { GroupChildrenState } from "@design-system/ViewTable/grouping/groupByRowTypes";
import { accountItemRowId } from "./integrationsRowId";

interface SelectedReminderTargetsParams {
    /** Selected row ids, as the table stores them (`item:<id>` for items). */
    rowIds: string[];
    childrenByKey: Record<
        string,
        GroupChildrenState<OrganizationIntegrationAccountItem> | undefined
    >;
    /** `null` while the session is still loading — nothing is eligible yet. */
    currentUserId: number | null;
}

export interface SelectedReminderTargets {
    /**
     * Selected accounts that can be nudged. This is the N in "Send email (N)":
     * the ticket counts accounts, because that is what the user selected.
     */
    eligibleAccountCount: number;
    /**
     * The logins those accounts belong to — one email each, so this is almost
     * always shorter than `eligibleAccountCount`.
     */
    connectionIds: string[];
}

/**
 * Works out who can actually be reminded from a raw row selection.
 *
 * Status and `connectedBy` live on the *connection*, not the account, so
 * several selected rows can collapse to one recipient. Counting accounts but
 * sending per connection is deliberate: the count reflects what the user
 * selected, while the sends reflect who gets an email.
 *
 * Rows are resolved out of `childrenByKey`; ids that no longer resolve (their
 * group was collapsed or paged away) are dropped rather than treated as errors.
 */
export const selectedReminderTargets = ({
    rowIds,
    childrenByKey,
    currentUserId,
}: SelectedReminderTargetsParams): SelectedReminderTargets => {
    if (currentUserId === null) {
        return { eligibleAccountCount: 0, connectionIds: [] };
    }

    const selectedIds = new Set(rowIds);
    const itemsById = new Map<string, OrganizationIntegrationAccountItem>();
    for (const state of Object.values(childrenByKey)) {
        for (const item of state?.items ?? []) {
            itemsById.set(accountItemRowId(item), item);
        }
    }

    const connectionIds = new Set<string>();
    let eligibleAccountCount = 0;

    for (const rowId of selectedIds) {
        const item = itemsById.get(rowId);
        if (!item) {
            continue;
        }
        const { connection } = item;
        if (connection.status !== "disconnected") {
            continue;
        }
        // A connection with no known creator has nobody to email. `userId` is
        // the only trustworthy comparison — emails are nullable on both sides.
        if (
            connection.connectedBy?.userId === undefined ||
            connection.connectedBy.userId === null ||
            connection.connectedBy.userId === currentUserId
        ) {
            continue;
        }

        eligibleAccountCount += 1;
        connectionIds.add(connection.id);
    }

    return {
        eligibleAccountCount,
        connectionIds: [...connectionIds],
    };
};
